import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, stationViewApi, stationsApi } from '../api/client';
import { useKitchenWsConnected } from './useKitchenWsStatus';
import { acquireOrdersSync, ordersSyncFor } from '../lib/sync/orders-sync-registry';

/**
 * How often a poll re-reads a whole order list where the changes feed is not
 * used: the station view, and the board with a status filter. (The live board
 * polls the feed every 2 s and falls back to these same intervals — see
 * lib/sync/orders-sync.ts.)
 *
 * The list is the heaviest thing the display asks for — about 44 KB a call on a
 * busy afternoon — and the socket already announces every new order, every
 * status change and every fired wave. So while the socket is up the poll only
 * has to catch what the socket could have dropped, and fifteen seconds is short
 * enough that nothing sits unseen on a screen in a kitchen.
 *
 * With no socket the poll IS how a comanda reaches the cook, and goes back to
 * being quick. Measured at Sun Club on 18/09/2026: the five-second poll alone
 * was 31 MB an hour per screen and kept a Raspberry's browser at a full core
 * with nothing happening in the venue.
 */
const ORDERS_POLL_WITH_SOCKET_MS = 15_000;
const ORDERS_POLL_WITHOUT_SOCKET_MS = 5_000;

/**
 * The order board's list.
 *
 * Without a status filter (the live board) this is fed by the sync loop of
 * feature 334: one full read, then a 2 s changes feed patching the cache (see
 * lib/sync/orders-sync.ts) — no per-query polling. With a status filter it is
 * the plain query it always was.
 */
export function useOrders(clientId: string | null, status?: string) {
  const socketUp = useKitchenWsConnected();
  const queryClient = useQueryClient();
  const synced = status === undefined;

  useEffect(() => {
    if (!clientId || !synced) return;
    return acquireOrdersSync(clientId, queryClient);
  }, [clientId, synced, queryClient]);

  return useQuery({
    queryKey: ['kitchen-orders', clientId, status],
    queryFn: synced
      ? () => ordersSyncFor(clientId!, queryClient).fetchFull()
      : () => ordersApi.list(clientId!, { status }),
    enabled: !!clientId,
    ...(synced
      ? { staleTime: Infinity, retry: false, refetchOnWindowFocus: false }
      : {
          refetchInterval: socketUp
            ? ORDERS_POLL_WITH_SOCKET_MS
            : ORDERS_POLL_WITHOUT_SOCKET_MS,
        }),
  });
}

export function useStationOrders(stationId: string | null, clientId: string | null) {
  const socketUp = useKitchenWsConnected();
  return useQuery({
    queryKey: ['kitchen-station-orders', stationId, clientId],
    queryFn: () => stationViewApi.get(stationId!, clientId!),
    enabled: !!stationId && !!clientId,
    refetchInterval: socketUp
      ? ORDERS_POLL_WITH_SOCKET_MS
      : ORDERS_POLL_WITHOUT_SOCKET_MS,
  });
}

// Feature 148 (US2) — Storico ondate del cuoco.
// Query ISOLATA dalla vista live: queryKey distinta e NESSUN refetchInterval
// (snapshot on-demand, FR-010a). `enabled` solo quando il toggle "Storico" è
// attivo. `refetch` alimenta il pulsante "Aggiorna".
//
// `getOrders` senza `date` filtra su numberDate = giorno UTC corrente, che rolla
// a mezzanotte UTC (≈ 02:00 in Italia), NON al cutoff di giornata lavorativa
// (es. 04:00). Dopo le 2 di notte le comande della serata (bucket di IERI UTC)
// sparivano dallo Storico "come se fosse già il giorno dopo". Per coprire la
// giornata di servizio a cavallo della mezzanotte UTC uniamo il bucket di OGGI
// e quello di IERI (UTC).
export function useHistoryOrders(clientId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['kitchen-history-orders', clientId],
    queryFn: async () => {
      const yesterday = new Date(Date.now() - 86_400_000)
        .toISOString()
        .slice(0, 10);
      const [todayOrders, yesterdayOrders] = await Promise.all([
        ordersApi.list(clientId!),
        ordersApi.list(clientId!, { date: yesterday }),
      ]);
      return [...todayOrders, ...yesterdayOrders];
    },
    enabled: !!clientId && enabled,
    // Snapshot: nessun polling. L'aggiornamento avviene solo via refetch manuale.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useStations(clientId: string | null) {
  return useQuery({
    queryKey: ['kitchen-stations', clientId],
    queryFn: () => stationsApi.list(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}
